import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LiveActivityDashboard from './components/LiveActivityDashboard';
import AccessControlPanel from './components/AccessControlPanel';
import CriticalAlertsPanel from './components/CriticalAlertsPanel';

const RealTimeMemberAccessControl = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, loading: authLoading, profileLoading } = useAuth();
  const [facilityStats, setFacilityStats] = useState({
    currentOccupancy: 0,
    activeCheckIns: 0,
    accessAttempts: 0,
    capacityLimit: 100
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [criticalAlerts, setCriticalAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    // Wait for both auth and profile to load before checking permissions
    if (!authLoading && !profileLoading) {
      if (!user || !isStaffMember) {
        navigate('/login');
      }
    }
  }, [user, isStaffMember, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isStaffMember) {
      loadFacilityData();
      setupRealtimeSubscription();
    }
  }, [currentTenant?.id, isStaffMember]);

  const loadFacilityData = async () => {
    setLoading(true);
    setError('');

    try {
      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      // Get today's attendance logs with member details
      const { data: attendanceLogs, error: attendanceError } = await supabase?.from('attendance_logs')?.select(`
          *,
          user_profiles!attendance_logs_user_id_fkey(id, full_name, email, avatar_url),
          member_barcodes(barcode_data, barcode_type)
        `)?.eq('tenant_id', currentTenant?.id)?.gte('check_in_time', today?.toISOString())?.order('check_in_time', { ascending: false })?.limit(50);

      if (attendanceError) throw attendanceError;

      // Calculate current occupancy (checked in but not checked out)
      const currentOccupancy = attendanceLogs?.filter(log => !log?.check_out_time)?.length || 0;

      setFacilityStats({
        currentOccupancy,
        activeCheckIns: attendanceLogs?.length || 0,
        accessAttempts: attendanceLogs?.length || 0,
        capacityLimit: 100
      });

      setRecentActivity(attendanceLogs || []);

      // Load critical alerts
      await loadCriticalAlerts();
    } catch (err) {
      setError(err?.message || 'Failed to load facility data');
    } finally {
      setLoading(false);
    }
  };

  const loadCriticalAlerts = async () => {
    try {
      // Get members with expired or suspended memberships
      const { data: members, error: membersError } = await supabase?.from('tenant_members')?.select(`
          *,
          user_profiles(full_name, email),
          member_subscriptions(status, end_date)
        `)?.eq('tenant_id', currentTenant?.id)?.or('is_active.eq.false,member_subscriptions.status.eq.expired,member_subscriptions.status.eq.suspended');

      if (membersError) throw membersError;

      const alerts = members?.map(member => ({
        id: member?.id,
        type: !member?.is_active ? 'suspended' : 'expired',
        memberName: member?.user_profiles?.full_name,
        memberEmail: member?.user_profiles?.email,
        message: !member?.is_active 
          ? 'Account suspended - access denied' :'Membership expired - requires renewal',
        timestamp: new Date()?.toISOString(),
        severity: 'high'
      })) || [];

      setCriticalAlerts(alerts);
    } catch (err) {
      console.error('Error loading critical alerts:', err);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase?.channel('attendance-realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'attendance_logs',
          filter: `tenant_id=eq.${currentTenant?.id}`
        },
        (payload) => {
          console.log('Real-time update:', payload);
          loadFacilityData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const handleGrantAccess = async (userId) => {
    try {
      const { error } = await supabase?.from('tenant_members')?.update({ is_active: true })?.eq('user_id', userId)?.eq('tenant_id', currentTenant?.id);

      if (error) throw error;

      loadFacilityData();
    } catch (err) {
      console.error('Error granting access:', err);
    }
  };

  const handleDenyAccess = async (userId) => {
    try {
      const { error } = await supabase?.from('tenant_members')?.update({ is_active: false })?.eq('user_id', userId)?.eq('tenant_id', currentTenant?.id);

      if (error) throw error;

      loadFacilityData();
    } catch (err) {
      console.error('Error denying access:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading access control...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isStaffMember) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-card rounded-lg p-8 text-center">
            <Icon name="Lock" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
            <p className="text-muted-foreground">
              This feature is only available to gym staff and owners.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-[1440px] px-6 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                Real-Time Member Access Control
              </h1>
              <p className="text-muted-foreground">
                Monitor facility check-ins, manage access permissions, and enforce membership restrictions
              </p>
            </div>
            <Button onClick={loadFacilityData} variant="outline" className="flex items-center gap-2">
              <Icon name="RefreshCw" size={20} />
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {/* Facility Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="Users" size={24} color="var(--color-primary)" />
              <span className="text-3xl font-bold text-foreground">{facilityStats?.currentOccupancy}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Current Occupancy</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {Math.round((facilityStats?.currentOccupancy / facilityStats?.capacityLimit) * 100)}% capacity
            </p>
          </div>

          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="LogIn" size={24} color="var(--color-success)" />
              <span className="text-3xl font-bold text-foreground">{facilityStats?.activeCheckIns}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Active Check-Ins</h3>
            <p className="text-xs text-muted-foreground mt-1">Today's total</p>
          </div>

          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="Activity" size={24} color="var(--color-accent)" />
              <span className="text-3xl font-bold text-foreground">{facilityStats?.accessAttempts}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Access Attempts</h3>
            <p className="text-xs text-muted-foreground mt-1">All attempts today</p>
          </div>

          <div className="bg-card rounded-xl shadow-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
              <span className="text-3xl font-bold text-foreground">{criticalAlerts?.length}</span>
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">Critical Alerts</h3>
            <p className="text-xs text-muted-foreground mt-1">Requires attention</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Live Activity Dashboard - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            <LiveActivityDashboard 
              recentActivity={recentActivity}
              onRefresh={loadFacilityData}
            />
            <AccessControlPanel
              onGrantAccess={handleGrantAccess}
              onDenyAccess={handleDenyAccess}
              tenantId={currentTenant?.id}
            />
          </div>

          {/* Critical Alerts Panel - Takes 1 column */}
          <div className="lg:col-span-1">
            <CriticalAlertsPanel
              alerts={criticalAlerts}
              onRefresh={loadCriticalAlerts}
            />
          </div>
        </div>
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default RealTimeMemberAccessControl;